import CategoryFilter from "@/components/filters/CategoryFilter";
import LocationFilter from "@/components/filters/LocationFilter";
import PriceRangeFilter from "@/components/filters/PriceRangeFilter";
import SqftRangeFilter from "@/components/filters/SqftRangeFilter";
import PropertyTypeFilter from "@/components/filters/PropertyTypeFilter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import {
  ListingFilters,
  ListingType,
  Province,
  District,
  City,
} from "@/lib/types";
import {
  useProvinces,
  useAllDistricts,
  useAllCities,
} from "@/hooks/useLocations";
import { useCallback, useEffect, useState, useMemo } from "react";
import ActiveFiltersDisplay from "@/components/search/ActiveFiltersDisplay";
import { useListingFilters } from "@/hooks/useListingFilters"; // Import useListingFilters

// Define the structure for filters managed by this component internally
// Helper to get display name and type (province, district, city) from slug
const getLocationTypeAndName = (
  slug: string,
  provinces: Province[],
  districts: District[],
  cities: City[]
) => {
  const province = provinces.find((p) => p.slug === slug);
  if (province)
    return { type: "province", name: province.name, id: province.id };
  const district = districts.find((d) => d.slug === slug);
  if (district)
    return {
      type: "district",
      name: district.name,
      id: district.id,
      province_id: district.province_id,
    };
  const city = cities.find((c) => c.slug === slug);
  if (city)
    return {
      type: "city",
      name: city.name,
      id: city.id,
      district_id: city.district_id,
    };
  return null;
};

// Define the structure for filters managed by this component internally
interface InternalManagedFilters {
  categories?: ListingType[];
  types?: string[];
  locations?: string[]; // New: Array of all selected location slugs
  priceRange?: [number, number];
  sqftRange?: [number, number];
  featured?: boolean;
}

interface PropertyFiltersProps {
  onFilterChange: (filters: Partial<ListingFilters>) => void;
  initialFilters: ListingFilters;
}

export default function PropertyFilters({
  onFilterChange,
  initialFilters,
}: PropertyFiltersProps) {
  // Fetch all location data for mapping IDs to names for display in ActiveFiltersDisplay
  const { data: allProvincesData } = useProvinces();
  const { data: allDistrictsData } = useAllDistricts();
  const { data: allCitiesData } = useAllCities();

  // Destructure location-specific handlers from useListingFilters
  const {
    onSelectProvince,
    onSelectDistrict,
    onSelectCity,
    onClearAllLocations,
    clearFilters, // Import clearFilters from the hook
  } = useListingFilters();

  // Memoize the derived selectedProvince, District, City for LocationFilter
  const derivedSelectedLocations = useMemo(() => {
    const selectedSlugs = initialFilters.location || [];
    let province: string | null = null;
    let district: string | null = null;
    let city: string | null = null;

    // Iterate through selected slugs to find the most specific hierarchical match
    // This assumes a single selection per level for the filter panel UI
    for (const slug of selectedSlugs) {
      const locInfo = getLocationTypeAndName(
        slug,
        allProvincesData || [],
        allDistrictsData || [],
        allCitiesData || []
      );
      if (locInfo) {
        if (locInfo.type === "province") {
          province = slug;
          // If a province is found, check if a district/city within it is also selected
          const matchingDistrict = allDistrictsData?.find(
            (d) =>
              d.slug === selectedSlugs.find((s) => d.slug === s) &&
              d.province_id === locInfo.id
          );
          if (matchingDistrict) {
            district = matchingDistrict.slug;
            const matchingCity = allCitiesData?.find(
              (c) =>
                c.slug === selectedSlugs.find((s) => c.slug === s) &&
                c.district_id === matchingDistrict.id
            );
            if (matchingCity) {
              city = matchingCity.slug;
            }
          }
        } else if (locInfo.type === "district") {
          district = slug;
          // If a district is found, ensure its parent province is also selected or derive it
          const parentProvince = allProvincesData?.find(
            (p) => p.id === locInfo.province_id
          );
          if (parentProvince) province = parentProvince.slug;
          const matchingCity = allCitiesData?.find(
            (c) =>
              c.slug === selectedSlugs.find((s) => c.slug === s) &&
              c.district_id === locInfo.id
          );
          if (matchingCity) {
            city = matchingCity.slug;
          }
        } else if (locInfo.type === "city") {
          city = slug;
          // If a city is found, ensure its parent district and province are also selected or derive them
          const parentDistrict = allDistrictsData?.find(
            (d) => d.id === locInfo.district_id
          );
          if (parentDistrict) {
            district = parentDistrict.slug;
            const parentProvince = allProvincesData?.find(
              (p) => p.id === parentDistrict.province_id
            );
            if (parentProvince) province = parentProvince.slug;
          }
        }
      }
    }
    return { province, district, city };
  }, [
    initialFilters.location,
    allProvincesData,
    allDistrictsData,
    allCitiesData,
  ]);

  const [managedFilters, setManagedFilters] = useState<InternalManagedFilters>(
    () => ({
      categories: initialFilters.listing_type || [],
      types: initialFilters.propertyType || [],
      locations: initialFilters.location || [], // Initialize with the flat array
      priceRange:
        initialFilters.minPrice || initialFilters.maxPrice
          ? [initialFilters.minPrice || 0, initialFilters.maxPrice || 50000000]
          : undefined,
      sqftRange:
        initialFilters.minSize || initialFilters.maxSize
          ? [initialFilters.minSize || 0, initialFilters.maxSize || 75000]
          : undefined,
      featured: initialFilters.featured,
    })
  );

  // Update managedFilters when initialFilters changes
  useEffect(() => {
    setManagedFilters({
      categories: initialFilters.listing_type || [],
      types: initialFilters.propertyType || [],
      locations: initialFilters.location || [], // Update with the flat array
      priceRange:
        initialFilters.minPrice || initialFilters.maxPrice
          ? [initialFilters.minPrice || 0, initialFilters.maxPrice || 50000000]
          : undefined,
      sqftRange:
        initialFilters.minSize || initialFilters.maxSize
          ? [initialFilters.minSize || 0, initialFilters.maxSize || 75000]
          : undefined,
      featured: initialFilters.featured,
    });
  }, [initialFilters]);

  // Generic handler to update a part of the local state and call onFilterChange
  const updateAndNotify = useCallback(
    (update: Partial<InternalManagedFilters>) => {
      setManagedFilters((prev) => {
        const newInternalState = { ...prev, ...update };

        const filtersToNotify: Partial<ListingFilters> = {
          listing_type: newInternalState.categories,
          propertyType: newInternalState.types,
          location: newInternalState.locations, // Pass the flat locations array
          minPrice: newInternalState.priceRange?.[0],
          maxPrice: newInternalState.priceRange?.[1],
          minSize: newInternalState.sqftRange?.[0],
          maxSize: newInternalState.sqftRange?.[1],
          featured: newInternalState.featured,
        };
        if (
          filtersToNotify.minPrice === 0 &&
          filtersToNotify.maxPrice === 50000000 &&
          !newInternalState.priceRange
        ) {
          delete filtersToNotify.minPrice;
          delete filtersToNotify.maxPrice;
        }
        if (
          filtersToNotify.minSize === 0 &&
          filtersToNotify.maxSize === 75000 &&
          !newInternalState.sqftRange
        ) {
          delete filtersToNotify.minSize;
          delete filtersToNotify.maxSize;
        }
        onFilterChange(filtersToNotify);
        return newInternalState;
      });
    },
    [onFilterChange]
  );

  // Specific handler for multi-select toggling (used by Category and PropertyType filters)
  const handleToggleMultiSelect = useCallback(
    (
      filterKey: keyof Pick<InternalManagedFilters, "categories" | "types">,
      value: string
    ) => {
      setManagedFilters((prev) => {
        const currentValues = prev[filterKey] || [];
        const newValues = (currentValues as string[]).includes(value)
          ? (currentValues as string[]).filter((item) => item !== value)
          : [...(currentValues as string[]), value];

        const newState = { ...prev, [filterKey]: newValues };

        const filtersToNotify: Partial<ListingFilters> = {
          listing_type: newState.categories,
          propertyType: newState.types,
          location: newState.locations, // Pass the flat locations array
          featured: newState.featured,
        };
        onFilterChange(filtersToNotify);
        return newState;
      });
    },
    [onFilterChange]
  );

  // Clear All handler
  const handleClearAll = useCallback(() => {
    const clearedInternalState: InternalManagedFilters = {
      categories: [],
      types: [],
      locations: [], // Clear the flat locations array
      priceRange: undefined,
      sqftRange: undefined,
      featured: undefined,
    };
    setManagedFilters(clearedInternalState);

    const filtersToNotify: Partial<ListingFilters> = {
      listing_type: [],
      propertyType: [],
      location: [], // Notify with empty locations array
      minPrice: undefined,
      maxPrice: undefined,
      minSize: undefined,
      maxSize: undefined,
      featured: undefined,
    };
    onFilterChange(filtersToNotify);
    clearFilters(); // Call the comprehensive clearFilters from useListingFilters
  }, [onFilterChange, clearFilters]);

  // Handler for removing individual active filters
  const handleRemoveFilter = useCallback(
    (
      type:
        | "propertyType"
        | "listingType"
        | "province"
        | "district"
        | "city"
        | "priceRange"
        | "sqftRange"
        | "featured",
      value?: string | number
    ) => {
      setManagedFilters((prev) => {
        let newState = { ...prev };
        switch (type) {
          case "propertyType":
            newState.types = (newState.types || []).filter((t) => t !== value);
            break;
          case "listingType":
            newState.categories = (newState.categories || []).filter(
              (c) => c !== value
            );
            break;
          case "province":
            // Remove province and all its children from locations
            const provinceToRemove = allProvincesData?.find(
              (p) => p.slug === value
            );
            if (provinceToRemove) {
              const districtsInProvince = allDistrictsData
                ?.filter((d) => d.province_id === provinceToRemove.id)
                .map((d) => d.slug);
              const citiesInProvince = allCitiesData
                ?.filter((c) =>
                  districtsInProvince?.includes(
                    allDistrictsData?.find((d) => d.id === c.district_id)?.slug
                  )
                )
                .map((c) => c.slug);
              newState.locations = (newState.locations || []).filter(
                (slug) =>
                  slug !== value && // Remove province slug
                  !districtsInProvince?.includes(slug) && // Remove its districts
                  !citiesInProvince?.includes(slug) // Remove its cities
              );
            }
            break;
          case "district":
            // Remove district and its children cities from locations
            const districtToRemove = allDistrictsData?.find(
              (d) => d.slug === value
            );
            if (districtToRemove) {
              const citiesInDistrict = allCitiesData
                ?.filter((c) => c.district_id === districtToRemove.id)
                .map((c) => c.slug);
              newState.locations = (newState.locations || []).filter(
                (slug) =>
                  slug !== value && // Remove district slug
                  !citiesInDistrict?.includes(slug) // Remove its cities
              );
            }
            break;
          case "city":
            newState.locations = (newState.locations || []).filter(
              (slug) => slug !== value
            );
            break;
          case "priceRange":
            newState.priceRange = undefined;
            break;
          case "sqftRange":
            newState.sqftRange = undefined;
            break;
          case "featured":
            newState.featured = undefined;
            break;
        }
        // Notify parent of the change
        const filtersToNotify: Partial<ListingFilters> = {
          listing_type: newState.categories,
          propertyType: newState.types,
          location: newState.locations, // Pass the flat locations array
          minPrice: newState.priceRange?.[0],
          maxPrice: newState.priceRange?.[1],
          minSize: newState.sqftRange?.[0],
          maxSize: newState.sqftRange?.[1],
          featured: newState.featured,
        };
        onFilterChange(filtersToNotify);
        return newState;
      });
    },
    [onFilterChange, allProvincesData, allDistrictsData, allCitiesData]
  );

  return (
    <Card className="p-0 overflow-hidden">
      <div className="space-y-6">
        {/* Active Filters Display */}
        <ActiveFiltersDisplay
          propertyTypes={managedFilters.types || []}
          listingTypes={managedFilters.categories || []}
          provinceSlug={derivedSelectedLocations.province}
          districtSlug={derivedSelectedLocations.district}
          citySlug={derivedSelectedLocations.city}
          priceRange={
            managedFilters.priceRange
              ? {
                  min: managedFilters.priceRange[0],
                  max: managedFilters.priceRange[1],
                }
              : undefined
          }
          sqftRange={
            managedFilters.sqftRange
              ? {
                  min: managedFilters.sqftRange[0],
                  max: managedFilters.sqftRange[1],
                }
              : undefined
          }
          featured={managedFilters.featured}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAll}
        />

        <CategoryFilter
          selectedCategories={managedFilters.categories || []}
          onToggleCategory={(value) =>
            handleToggleMultiSelect("categories", value)
          }
          onClearCategories={() => updateAndNotify({ categories: [] })}
        />
        <PropertyTypeFilter
          selectedTypes={managedFilters.types || []}
          onToggleType={(value) => handleToggleMultiSelect("types", value)}
          onClearTypes={() => updateAndNotify({ types: [] })}
        />
        <LocationFilter
          selectedProvince={derivedSelectedLocations.province}
          onSelectProvince={onSelectProvince}
          selectedDistrict={derivedSelectedLocations.district}
          onSelectDistrict={onSelectDistrict}
          selectedCity={derivedSelectedLocations.city}
          onSelectCity={onSelectCity}
          onClearAllLocations={onClearAllLocations}
        />
        <PriceRangeFilter
          min={0}
          max={50000000}
          step={100000}
          initialMin={managedFilters.priceRange?.[0] || 0}
          initialMax={managedFilters.priceRange?.[1] || 50000000}
          onPriceChange={(min, max) =>
            updateAndNotify({ priceRange: [min, max] })
          }
        />
        <SqftRangeFilter
          min={0}
          max={75000}
          step={50}
          initialMin={managedFilters.sqftRange?.[0] || 0}
          initialMax={managedFilters.sqftRange?.[1] || 75000}
          onSqftChange={(min, max) =>
            updateAndNotify({ sqftRange: [min, max] })
          }
        />
        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <Label htmlFor="featured-switch">Featured Only</Label>
          <Switch
            id="featured-switch"
            checked={!!managedFilters.featured}
            onCheckedChange={(checked) =>
              updateAndNotify({ featured: checked || undefined })
            }
          />
        </div>
        <div className="p-4 border-t border-gray-200">
          <Button variant="outline" className="w-full" onClick={handleClearAll}>
            Clear All Filters
          </Button>
        </div>
      </div>
    </Card>
  );
}
